import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { BrandProvider } from "@/hooks/useBrand";
import ProtectedRoute from "@/components/ProtectedRoute";
import OfflineIndicator from "@/components/OfflineIndicator";
import { Loader2 } from "lucide-react";

const Auth = lazy(() => import("./pages/Auth"));
const RoleRedirect = lazy(() => import("./pages/RoleRedirect"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminPools = lazy(() => import("./pages/admin/Pools"));
const AdminStaff = lazy(() => import("./pages/admin/Staff"));
const AdminInventory = lazy(() => import("./pages/admin/Inventory"));
const AdminReports = lazy(() => import("./pages/admin/Reports"));
const AdminCalculator = lazy(() => import("./pages/admin/Calculator"));
const AdminFinancial = lazy(() => import("./pages/admin/Financial"));
const AdminFinancialContracts = lazy(() => import("./pages/admin/FinancialContracts"));
const AdminFinancialTransactions = lazy(() => import("./pages/admin/FinancialTransactions"));
const AdminFinancialProfitability = lazy(() => import("./pages/admin/FinancialProfitability"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const StaffAgenda = lazy(() => import("./pages/staff/Agenda"));
const ServiceOrderPage = lazy(() => import("./pages/staff/ServiceOrder"));

const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const AppFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
        <BrandProvider>
          <Suspense fallback={<AppFallback />}>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/aguardando-aprovacao" element={<PendingApproval />} />
              <Route path="/" element={<ProtectedRoute><RoleRedirect /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/pools" element={<ProtectedRoute requiredRole="admin"><AdminPools /></ProtectedRoute>} />
              <Route path="/admin/staff" element={<ProtectedRoute requiredRole="admin"><AdminStaff /></ProtectedRoute>} />
              <Route path="/admin/inventory" element={<ProtectedRoute requiredRole="admin"><AdminInventory /></ProtectedRoute>} />
              <Route path="/admin/reports" element={<ProtectedRoute requiredRole="admin"><AdminReports /></ProtectedRoute>} />
              <Route path="/admin/calculadora" element={<ProtectedRoute requiredRole="admin"><AdminCalculator /></ProtectedRoute>} />
              <Route path="/admin/financeiro" element={<ProtectedRoute requiredRole="admin"><AdminFinancial /></ProtectedRoute>} />
              <Route path="/admin/financeiro/contratos" element={<ProtectedRoute requiredRole="admin"><AdminFinancialContracts /></ProtectedRoute>} />
              <Route path="/admin/financeiro/lancamentos" element={<ProtectedRoute requiredRole="admin"><AdminFinancialTransactions /></ProtectedRoute>} />
              <Route path="/admin/financeiro/rentabilidade" element={<ProtectedRoute requiredRole="admin"><AdminFinancialProfitability /></ProtectedRoute>} />
              <Route path="/admin/configuracoes" element={<ProtectedRoute requiredRole="admin"><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin/order/:id" element={<ProtectedRoute requiredRole="admin"><ServiceOrderPage /></ProtectedRoute>} />
              <Route path="/tecnico" element={<ProtectedRoute requiredRole="staff"><StaffAgenda /></ProtectedRoute>} />
              <Route path="/tecnico/order/:id" element={<ProtectedRoute requiredRole="staff"><ServiceOrderPage /></ProtectedRoute>} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <OfflineIndicator />
        </BrandProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
